/**
 * Содержание поп-апа «что нового» для задеплоенной версии (#946).
 *
 * Приходит из `docs/releases/whats-new/<version>.json`, вшитого в бандл на
 * сборке (`vite.config.ts`, define `__WHATS_NEW__`). Не запрос к API: поп-ап
 * рассказывает ровно о той версии, которую человек сейчас открыл, и лишний
 * круг к сети отодвинул бы первый экран ради текста, который уже здесь.
 *
 * `null` значит «показывать нечего»: релиз без клиентских изменений (`skip`),
 * сборка без define (vitest) или отсутствующий файл. Следит за тем, чтобы это
 * решение было осознанным, гейт `npm run check:whats-new`.
 */
import type { WhatsNewRelease } from '@aica/api-types';
import { normalizeDocsLang } from '@/lib/docs';

declare const __WHATS_NEW__: WhatsNewRelease | null | undefined;

export const WHATS_NEW: WhatsNewRelease | null =
  typeof __WHATS_NEW__ !== 'undefined' ? (__WHATS_NEW__ ?? null) : null;

export type WhatsNewLocale = 'en' | 'ru' | 'uk' | 'es';

/**
 * Локаль поп-апа из локали интерфейса.
 *
 * Правило то же, что у ссылок на документацию (`normalizeDocsLang`), и берётся
 * оттуда же: набор локалей один, и вторая его копия разошлась бы с первой.
 * Незнакомый язык — английский: пустой поп-ап хуже поп-апа не на родном языке.
 */
export function resolveWhatsNewLocale(language: string | undefined): WhatsNewLocale {
  return normalizeDocsLang(language) as WhatsNewLocale;
}

/**
 * Показывать ли поп-ап этого релиза.
 *
 * Три «нет», и каждое — отдельный случай, который иначе кончается лишним
 * показом:
 *  - контента нет (инфраструктурный релиз или сборка без define);
 *  - эту версию уже видели;
 *  - пользователь появился ПОСЛЕ выката — новость о том, что было до него,
 *    ему не новость. Даты сравниваются по дню: в артефакте дата выката без
 *    времени, и сравнение с точностью до часа было бы ложной точностью.
 */
export function shouldShowWhatsNew(params: {
  release: WhatsNewRelease | null;
  lastSeenRelease: string | null | undefined;
  userCreatedAt: string | null | undefined;
}): boolean {
  const { release, lastSeenRelease, userCreatedAt } = params;
  if (!release) return false;
  if (lastSeenRelease === release.version) return false;

  if (userCreatedAt) {
    const created = userCreatedAt.slice(0, 10);
    // Строковое сравнение ISO-дат корректно и не зависит от часового пояса,
    // в котором окажется браузер.
    if (created > release.date) return false;
  }

  return true;
}
